import type { ActivityKind } from "@visioni/a3s-activity-recorder/types";

/**
 * Filter state for the activity log screen. All fields are optional — unset
 * fields mean "no filter" for that dimension.
 */
export interface ActivityFilters {
  readonly kinds: readonly ActivityKind[];
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly cameraKey: string | null;
  readonly siteId: string | null;
  readonly userEmail: string | null;
}

export function getDefaultActivityFilters(): ActivityFilters {
  return {
    kinds: [],
    startDate: null,
    endDate: null,
    cameraKey: null,
    siteId: null,
    userEmail: null,
  };
}

/**
 * Encode ALL filter keys as route params (empty string for cleared values).
 * `router.setParams` shallow-merges so every key must be emitted to overwrite
 * stale values from previous commits.
 */
export function filtersToRouteParams(filters: ActivityFilters): Record<string, string> {
  return {
    kinds: filters.kinds.join(","),
    startDate: filters.startDate ?? "",
    endDate: filters.endDate ?? "",
    cameraKey: filters.cameraKey ?? "",
    siteId: filters.siteId ?? "",
    userEmail: filters.userEmail ?? "",
  };
}

/**
 * Decode URL/navigation params into a full filter set.
 * Empty strings in the URL represent cleared filters (null).
 */
export function paramsToFilters(params: Record<string, string | string[] | undefined>): ActivityFilters {
  const str = (v: string | string[] | undefined): string => (Array.isArray(v) ? (v[0] ?? "") : (v ?? ""));

  const kindsRaw = str(params["kinds"]);

  return {
    kinds: kindsRaw ? (kindsRaw.split(",").filter(Boolean) as ActivityKind[]) : [],
    startDate: str(params["startDate"]) || null,
    endDate: str(params["endDate"]) || null,
    cameraKey: str(params["cameraKey"]) || null,
    siteId: str(params["siteId"]) || null,
    userEmail: str(params["userEmail"]) || null,
  };
}

/**
 * Stable signature of a filter set for debouncing and change detection.
 * Keys are sorted so object insertion order is irrelevant.
 */
export function activityFilterSignature(filters: ActivityFilters): string {
  return stableStringify(filters);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}
